/**
 * Маршруты приложения.
 *
 * Кнопка «назад» у Telegram своя, поэтому внутри разделов навигации нет:
 * два механизма возврата на одном экране путают.
 */
import { useEffect } from 'react';
import { Route, Routes, useLocation, useNavigate } from 'react-router';

import { GuestNotice } from '@/features/guest/GuestNotice';
import { GiftPage } from '@/pages/gift/GiftPage';
import { GiftsPage } from '@/pages/gift/GiftsPage';
import { HelpPage } from '@/pages/help/HelpPage';
import { HomePage } from '@/pages/home/HomePage';
import { PacksPage } from '@/pages/packs/PacksPage';
import { PlansPage } from '@/pages/plans/PlansPage';
import { ProfilePage } from '@/pages/profile/ProfilePage';
import { RafflePage } from '@/pages/raffle/RafflePage';
import { RafflesPage } from '@/pages/raffle/RafflesPage';
import { WheelPage } from '@/pages/wheel/WheelPage';
import { ReferralPage } from '@/pages/referral/ReferralPage';
import { SubscriptionPage } from '@/pages/subscriptions/SubscriptionPage';
import { SubscriptionsPage } from '@/pages/subscriptions/SubscriptionsPage';
import { trackAppOpened, trackScreen } from '@/shared/lib/analytics';
import { isGuest } from '@/shared/lib/guest';
import { Header } from '@/shared/ui/kit/Header';
import { TabBar, type Tab } from '@/shared/ui/kit/TabBar';

/**
 * Вкладка, к которой относится экран.
 *
 * Вложенные экраны подсвечивают свой раздел: иначе на «Моих подписках»
 * не выделена ни одна вкладка и непонятно, где находишься.
 */
function tabFor(pathname: string): string {
  if (
    pathname.startsWith('/subscriptions') ||
    pathname.startsWith('/gift') ||
    pathname.startsWith('/packs')
  ) {
    return '/';
  }
  if (pathname.startsWith('/referral') || pathname.startsWith('/raffles') || pathname.startsWith('/wheel')) {
    return '/profile';
  }
  return pathname;
}

const TABS: readonly Tab[] = [
  { path: '/', label: 'Главная', icon: 'home' },
  { path: '/plans', label: 'Тарифы', icon: 'tag' },
  { path: '/profile', label: 'Профиль', icon: 'user' },
];

// Экраны аккаунта в режиме просмотра: данных нет, есть приглашение в бота.
const GuestProfile = () => (
  <GuestNotice
    icon="user"
    title="Профиль — в Telegram"
    text="Подписки, устройства и история оплат привязаны к аккаунту Telegram. Откройте бота — вход без регистрации и паролей."
  />
);

const GuestReferral = () => (
  <GuestNotice
    icon="users"
    title="Приглашайте друзей"
    text="Личная реферальная ссылка и начисления — в Telegram-боте. Откройте его, чтобы получить свою ссылку."
  />
);

const GuestPacks = () => (
  <GuestNotice
    icon="tag"
    title="Гигабайты — в Telegram"
    text="Пакеты для сервера «Белый список» покупаются в Telegram-боте и привязаны к вашему аккаунту."
  />
);

export function AppRouter() {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    // Кнопка под сообщением бота об оплате открывает приложение с ?sub=<id>:
    // человек попадает сразу на оплаченную подписку, а не на главную.
    const params = new URLSearchParams(location.search);
    // Кнопка «Продлить подписку» под напоминанием — ?renew=<id>: сразу тарифы
    // с выбранной подпиской (пустое значение — просто тарифы).
    const renew = params.get('renew');
    if (renew !== null) {
      const extend = /^[0-9a-f-]{36}$/i.test(renew) ? `?extend=${renew}` : '';
      void navigate(`/plans${extend}`, { replace: true });
      return;
    }
    // Кнопки «Крутить рулетку» / «Открыть приложение» под сообщениями рулетки.
    if (params.get('wheel') !== null) {
      void navigate('/wheel', { replace: true });
      return;
    }
    // Кнопка «Итоги конкурса» под сообщением победителю.
    const raffle = params.get('raffle');
    if (raffle && /^[0-9a-f-]{36}$/i.test(raffle)) {
      void navigate(`/raffles/${raffle}`, { replace: true });
      return;
    }
    // Кнопка «Реферальная программа» под сообщением о начислении.
    if (params.get('referral') !== null) {
      void navigate('/referral', { replace: true });
      return;
    }
    // Кнопка «Докупить гигабайты» под уведомлением о «Белом списке».
    if (params.get('packs') !== null) {
      void navigate('/packs', { replace: true });
      return;
    }
    // Кнопка «Мои подарки» под сообщением бота о купленном подарке.
    if (params.get('gifts') !== null) {
      void navigate('/gifts', { replace: true });
      return;
    }
    const sub = params.get('sub');
    if (sub && /^[0-9a-f-]{36}$/i.test(sub)) {
      void navigate(`/subscriptions/${sub}`, { replace: true });
    }
    // Только при запуске: дальше адрес меняет сама навигация.
  }, []);

  useEffect(() => {
    trackAppOpened();
  }, []);

  useEffect(() => {
    trackScreen(location.pathname);
  }, [location.pathname]);

  useEffect(() => {
    // Прокрутка вверх при смене экрана: иначе новый экран открывается
    // на том же месте, где человек остановился на предыдущем.
    window.scrollTo(0, 0);
  }, [location.pathname]);

  return (
    <>
      <Header />

      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/plans" element={<PlansPage />} />
        <Route path="/help" element={<HelpPage />} />
        <Route path="/packs" element={isGuest() ? <GuestPacks /> : <PacksPage />} />
        <Route path="/profile" element={isGuest() ? <GuestProfile /> : <ProfilePage />} />
        <Route path="/referral" element={isGuest() ? <GuestReferral /> : <ReferralPage />} />
        <Route path="/subscriptions" element={<SubscriptionsPage />} />
        <Route path="/gift" element={<GiftPage />} />
        <Route path="/raffles" element={isGuest() ? <GuestProfile /> : <RafflesPage />} />
        <Route path="/wheel" element={isGuest() ? <GuestProfile /> : <WheelPage />} />
        <Route path="/raffles/:id" element={isGuest() ? <GuestProfile /> : <RafflePage />} />
        <Route path="/gifts" element={isGuest() ? <GuestProfile /> : <GiftsPage />} />
        <Route path="/subscriptions/:id" element={<SubscriptionPage />} />
        <Route path="*" element={<HomePage />} />
      </Routes>

      <TabBar
        tabs={TABS}
        current={tabFor(location.pathname)}
        onSelect={(path) => void navigate(path)}
      />
    </>
  );
}
